import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsService } from "../services/cardsService";
import {
  StatementPeriodMode,
  type CardStatementResponse,
  type StatementPeriodMode as StatementPeriodModeType,
} from "../types/card-statement.types";

export interface UseCardStatementArgs {
  cardId: string | undefined;
  mode: StatementPeriodModeType;
  /** `YYYY-MM`, used when mode=month. */
  month?: string;
  /** `YYYY-MM-DD`, used when mode=range. */
  from?: string;
  /** `YYYY-MM-DD`, used when mode=range. */
  to?: string;
}

export interface UseCardStatementResult {
  statement: CardStatementResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the statement for the selected period. A sequence ref guards against out-of-order responses when
 * the user switches period quickly. An incomplete range is not requested — the backend would reject it.
 */
export const useCardStatement = ({
  cardId,
  mode,
  month,
  from,
  to,
}: UseCardStatementArgs): UseCardStatementResult => {
  const [statement, setStatement] = useState<CardStatementResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const fetchStatement = useCallback(async () => {
    const incompleteMonth = mode === StatementPeriodMode.MONTH && !month;
    const incompleteRange = mode === StatementPeriodMode.RANGE && (!from || !to);

    if (!cardId || incompleteMonth || incompleteRange) {
      seqRef.current += 1;
      setIsLoading(false);
      return;
    }

    const seq = ++seqRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await CardsService.getCardStatement(cardId, {
        mode,
        month: mode === StatementPeriodMode.MONTH ? month : undefined,
        from: mode === StatementPeriodMode.RANGE ? from : undefined,
        to: mode === StatementPeriodMode.RANGE ? to : undefined,
      });
      if (seq !== seqRef.current) return;
      setStatement(response.data);
    } catch (err: unknown) {
      if (seq !== seqRef.current) return;
      setStatement(null);
      setError(HttpApiService.getErrorMessage(err) || "error");
    } finally {
      if (seq === seqRef.current) setIsLoading(false);
    }
  }, [cardId, mode, month, from, to]);

  useEffect(() => {
    void fetchStatement();
  }, [fetchStatement]);

  return { statement, isLoading, error, refetch: () => void fetchStatement() };
};
